// src/reports/ChargebacksReport.jsx — Reporting → Store Reports → Chargebacks
// (Eric, 2026-10-06). The status of every Shopify Payments dispute on the store,
// read live from Shopify on each load (chargebacks_report.py) and joined to the
// order cache; scoped by the caller's location permissions. Responding to a
// dispute stays in Shopify admin — every row links there.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Button, TextField, Banner, Badge } from '@shopify/polaris';
import { useAuthFetch } from '../AuthComponent';
import { paneHeightToFitScroller, ReportDateRange, ReportPicker } from '../Reporting';

const etDate = d => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const todayIso = () => etDate(new Date());
const daysAgoIso = n => { const d = new Date(); d.setDate(d.getDate() - n); return etDate(d); };
const defaultFrom = () => daysAgoIso(365);
const fmtDay = iso => { if (!iso) return '—'; const [y, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}/${y}`; };
const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtWhen = iso => iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) + ' ET' : '';

const STATUS_OPTIONS = [
    { label: 'All statuses', value: '' },
    { label: 'Open (needs response + under review)', value: 'open' },
    { label: 'Needs response', value: 'NEEDS_RESPONSE' },
    { label: 'Under review', value: 'UNDER_REVIEW' },
    { label: 'Won', value: 'WON' },
    { label: 'Lost', value: 'LOST' },
    { label: 'Accepted', value: 'ACCEPTED' },
    { label: 'Charge refunded', value: 'CHARGE_REFUNDED' },
];
const STATUS_TONE = { NEEDS_RESPONSE: 'critical', UNDER_REVIEW: 'attention', WON: 'success', LOST: 'critical', ACCEPTED: 'warning', CHARGE_REFUNDED: 'info' };

const STORE_LABELS = { soccerpost: 'Soccer Post', surf: 'Surf', rush: 'Rush', citysports: 'City Sports', b2b: 'B2B', u90: 'U90', stefans: 'Stefans' };
const COLUMNS = [
    { key: 'store_key',          label: 'Store',          allOnly: true },
    { key: 'order_name',         label: 'Order',          tip: 'Opens the order in Operations Hub; the Shopify link opens the dispute in Shopify admin' },
    { key: 'opened',             label: 'Opened',         tip: 'The day the customer’s bank opened the dispute (ET)' },
    { key: 'status',             label: 'Status',         tip: 'Needs response = evidence not sent yet; Under review = evidence sent, bank deciding; Won / Lost = decided' },
    { key: 'reason_label',       label: 'Reason',         tip: 'The reason the bank gave' },
    { key: 'amount',             label: 'Amount',         right: true, tip: 'The disputed amount' },
    { key: 'evidence_due',       label: 'Evidence due',   tip: 'The last day to submit evidence in Shopify admin' },
    { key: 'evidence_sent',      label: 'Evidence sent',  tip: 'The day evidence was submitted' },
    { key: 'finalized',          label: 'Finalized',      tip: 'The day the bank decided' },
    { key: 'customer_name',      label: 'Customer' },
    { key: 'order_date',         label: 'Order date' },
    { key: 'order_total',        label: 'Order total',    right: true },
    { key: 'fulfillment_status', label: 'Fulfillment' },
    { key: 'items_count',        label: 'Items',          right: true },
    { key: 'delivery_method',    label: 'Delivery' },
    { key: 'locations',          label: 'Location(s)',    tip: 'Every location the order’s fulfillment orders touched; the report is scoped by these' },
];

function Tile({ label, value, sub, tone }) {
    const bg = { ok: '#e8f5e9', warn: '#fff4e5', bad: '#fdecea', muted: '#f6f6f7' }[tone] || '#f6f6f7';
    return (
        <div style={{ background: bg, borderRadius: 10, padding: '10px 14px', minWidth: 150 }}>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
            <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{label}</div>
            {sub ? <div style={{ fontSize: 12, color: '#6d7175', marginTop: 4 }}>{sub}</div> : null}
        </div>
    );
}

export default function ChargebacksReport() {
    const fetchAuth = useAuthFetch();
    const { storeSlug } = useParams();
    const storeKey = storeSlug || 'soccerpost';
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState(searchParams.get('status') || '');
    const [q, setQ] = useState(searchParams.get('q') || '');
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || defaultFrom());
    const [dateTo, setDateTo] = useState(searchParams.get('to') || todayIso());
    const [sort, setSort] = useState({ key: searchParams.get('sort') || 'opened', dir: searchParams.get('dir') || 'desc' });
    const allStores = storeKey === 'all';
    const columns = useMemo(() => COLUMNS.filter(c => !c.allOnly || allStores), [allStores]);
    const [data, setData] = useState({ rows: [], summary: null, shopify_admin_bases: {}, errors: [], as_of: null });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const tableWrapRef = useRef(null);
    const [paneMaxH, setPaneMaxH] = useState(0);
    const [qDebounced, setQDebounced] = useState(q);
    useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);

    const load = useCallback(async () => {
        setBusy(true); setErr(null);
        try {
            const p = new URLSearchParams({ store: storeKey, status, date_from: dateFrom, date_to: dateTo, q: qDebounced });
            const r = await fetchAuth(`/api/reports/chargebacks?${p}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setData(j);
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }, [fetchAuth, storeKey, status, dateFrom, dateTo, qDebounced]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const p = new URLSearchParams(searchParams);
        const set = (k, v) => { if (v) p.set(k, v); else p.delete(k); };
        set('status', status); set('q', q);
        set('from', dateFrom === defaultFrom() ? '' : dateFrom); set('to', dateTo === todayIso() ? '' : dateTo);
        set('sort', sort.key === 'opened' ? '' : sort.key); set('dir', sort.dir === 'desc' ? '' : sort.dir);
        if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
    }, [status, q, dateFrom, dateTo, sort]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        const el = tableWrapRef.current; if (!el) return;
        const fit = () => setPaneMaxH(paneHeightToFitScroller(el));
        fit(); window.addEventListener('resize', fit); return () => window.removeEventListener('resize', fit);
    }, [data.rows.length]);

    const rows = useMemo(() => {
        const list = [...(data.rows || [])];
        const k = sort.key, dir = sort.dir === 'asc' ? 1 : -1;
        const val = r => { const v = r[k]; return Array.isArray(v) ? v.join(', ') : (v === null || v === undefined ? '' : v); };
        list.sort((a, b) => { const x = val(a), y = val(b); if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir; return String(x).localeCompare(String(y)) * dir; });
        return list;
    }, [data.rows, sort]);
    const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'opened' ? 'desc' : 'asc' });
    const exportCsv = () => {
        const p = new URLSearchParams({ store: storeKey, status, date_from: dateFrom, date_to: dateTo, q: qDebounced });
        fetchAuth(`/api/reports/chargebacks/export.csv?${p}`).then(async r => {
            if (!r.ok) { let m = `HTTP ${r.status}`; try { m = (await r.json()).detail || m; } catch (_e) { /* not JSON */ } throw new Error(m); }
            return r.blob();
        }).then(b => {
            const a = document.createElement('a'); a.href = URL.createObjectURL(b);
            a.download = `chargebacks_${storeKey}_${dateFrom}_${dateTo}.csv`; a.click(); URL.revokeObjectURL(a.href);
        }).catch(e => setErr(e.message));
    };

    const s = data.summary;
    const by = s?.by_status || {};
    const cell = (extra) => ({ padding: '6px 8px', whiteSpace: 'nowrap', ...extra });
    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text as="h2" variant="headingMd">Chargebacks</Text>
                        <InlineStack gap="200" blockAlign="center">
                            {data.as_of && <Text as="span" tone="subdued">Live from Shopify at {fmtWhen(data.as_of)}</Text>}
                            <Button onClick={load} loading={busy}>Refresh</Button>
                            <Button onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
                        </InlineStack>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        Every Shopify Payments dispute on {allStores ? 'every store' : 'this store'}, read live from Shopify, with the order it belongs to. You see the disputes
                        whose orders touched your locations. Responding and uploading evidence happen in Shopify admin; the Shopify link on each
                        row opens the order there. The date range is the day the dispute was opened.
                    </Text>
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    {(data.errors || []).length > 0 && <Banner tone="warning">{data.errors.join(' · ')}</Banner>}
                    <InlineStack gap="300" blockAlign="end" wrap>
                        <ReportPicker label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
                        <div style={{ minWidth: 240 }}><TextField label="Search" labelHidden placeholder="Order, customer or tag…" value={q} onChange={setQ} autoComplete="off" clearButton onClearButtonClick={() => setQ('')} /></div>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} presets={['30d', '90d', 'ytd', 'last_ytd']} />
                    </InlineStack>
                    {s && (
                        <InlineGrid columns={{ xs: 2, md: 3, lg: 6 }} gap="300">
                            <Tile label="Needs response" value={fmtN(by.NEEDS_RESPONSE?.count)} tone={by.NEEDS_RESPONSE?.count ? 'bad' : 'muted'}
                                sub={[fmtUSD(by.NEEDS_RESPONSE?.amount), s.next_due ? `next due ${fmtDay(s.next_due)} (${s.next_due_order})` : null].filter(Boolean).join(' · ')} />
                            <Tile label="Under review" value={fmtN(by.UNDER_REVIEW?.count)} tone={by.UNDER_REVIEW?.count ? 'warn' : 'muted'} sub={fmtUSD(by.UNDER_REVIEW?.amount)} />
                            <Tile label="Won" value={fmtN(by.WON?.count)} tone="ok" sub={fmtUSD(by.WON?.amount)} />
                            <Tile label="Lost" value={fmtN(by.LOST?.count)} tone={by.LOST?.count ? 'bad' : 'muted'} sub={fmtUSD(by.LOST?.amount)} />
                            <Tile label="At risk" value={fmtUSD(s.at_risk_amount)} tone={s.at_risk_count ? 'warn' : 'muted'}
                                sub={`${fmtN(s.at_risk_count)} open${s.overdue_count ? ` · ${fmtN(s.overdue_count)} past the evidence deadline` : ''}`} />
                            <Tile label="Win rate" value={s.won_rate_pct === null || s.won_rate_pct === undefined ? '—' : `${s.won_rate_pct}%`} tone="muted"
                                sub={`${fmtN(s.total)} disputes · ${fmtUSD(s.total_amount)} in this view`} />
                        </InlineGrid>
                    )}
                </BlockStack>
            </Card>
            <Card padding="0">
                <div ref={tableWrapRef} style={{ overflow: 'auto', maxHeight: paneMaxH || undefined, clipPath: 'inset(0 round 12px)' }}>
                    <table className="report-table" style={{ minWidth: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                            <tr>
                                {columns.map(c => (
                                    <th key={c.key} onClick={() => toggleSort(c.key)} title={c.tip || undefined}
                                        style={{ cursor: 'pointer', textAlign: c.right ? 'right' : 'left', padding: '8px 8px', borderBottom: '1px solid #e1e3e5', fontWeight: 600, fontSize: 12 }}>
                                        {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={`${r.store_key}:${r.dispute_id}`} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                    {allStores && <td style={cell()}>{STORE_LABELS[r.store_key] || r.store_key}</td>}
                                    <td style={cell()}>
                                        {r.order_id ? <Link to={`/store/${r.store_key || storeKey}/orders/${r.order_id}?loc=all`}>{r.order_name || r.order_id}</Link> : (r.order_name || '—')}
                                        {r.order_id && data.shopify_admin_bases?.[r.store_key] && (
                                            <a href={`${data.shopify_admin_bases[r.store_key]}/orders/${r.order_id}`} target="_blank" rel="noopener noreferrer"
                                                title="Open in Shopify admin" style={{ marginLeft: 6, fontSize: 11, color: '#6d7175' }}>Shopify ↗</a>
                                        )}
                                        {r.cancelled ? <span style={{ marginLeft: 6 }}><Badge tone="warning">Cancelled</Badge></span> : null}
                                    </td>
                                    <td style={cell()}>{fmtDay(r.opened)}</td>
                                    <td style={cell()}>
                                        <Badge tone={STATUS_TONE[r.status] || 'info'}>{r.status_label}</Badge>
                                        {r.type === 'INQUIRY' ? <span style={{ marginLeft: 4 }}><Badge>Inquiry</Badge></span> : null}
                                    </td>
                                    <td style={cell()} title={r.network_reason_code ? `Network code ${r.network_reason_code}` : undefined}>{r.reason_label || '—'}</td>
                                    <td style={cell({ textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}>{fmtUSD(r.amount)}</td>
                                    <td style={cell()}>
                                        {fmtDay(r.evidence_due)}
                                        {r.status === 'NEEDS_RESPONSE' && r.days_left !== null && r.days_left !== undefined && (
                                            <span style={{ marginLeft: 6 }}>
                                                <Badge tone={r.overdue ? 'critical' : r.days_left <= 3 ? 'attention' : 'info'}>
                                                    {r.overdue ? `${Math.abs(r.days_left)} day${Math.abs(r.days_left) === 1 ? '' : 's'} overdue` : r.days_left === 0 ? 'Due today' : `${r.days_left} day${r.days_left === 1 ? '' : 's'} left`}
                                                </Badge>
                                            </span>
                                        )}
                                    </td>
                                    <td style={cell()}>{fmtDay(r.evidence_sent)}</td>
                                    <td style={cell()}>{fmtDay(r.finalized)}</td>
                                    <td style={cell()}>{r.customer_name || '—'}</td>
                                    <td style={cell()}>{fmtDay(r.order_date)}</td>
                                    <td style={cell({ textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}>{fmtUSD(r.order_total)}</td>
                                    <td style={cell()}>{r.fulfillment_status ? r.fulfillment_status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : '—'}</td>
                                    <td style={cell({ textAlign: 'right' })}>{fmtN(r.items_count)}</td>
                                    <td style={cell()}>{r.delivery_method || '—'}</td>
                                    <td style={cell()} title={r.expected_location ? `Expected: ${r.expected_location}` : undefined}>{r.locations?.length ? r.locations.join(', ') : (r.in_cache ? '—' : 'Not in the order cache')}</td>
                                </tr>
                            ))}
                            {!rows.length && !busy && (
                                <tr><td colSpan={columns.length} style={{ padding: 16, color: '#6d7175' }}>No disputes match.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </BlockStack>
    );
}
